import { createOne, fetchList, fetchOne } from '@/shared/api/jsonapi/client';
import {
  PROTECTED_SUB_TYPES, PROTECTED_SUB_TYPE_LABELS, PROTECTED_STATUS_TYPES, PROTECTED_STATUS_TYPE_LABELS,
} from '@/features/hr/protectedStatusVocab';
import { httpRequest } from '@/shared/api/client';
import { runWorkflowAction, type WorkflowResult, type WorkflowWarning } from '@/shared/api/workflow';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { createElement } from 'react';
import type { CrudSchema } from '@/shared/crud/schemaCrud';
import { enumCol, boolCol, dateCol } from '@/shared/crud/schemaCrud.utils';
import { LeaveAutoApproveNotice } from './LeaveAutoApproveNotice';
import { LeaveReasonRejectNotice } from './LeaveReasonRejectNotice';
import { APPROVAL_WORKFLOWS, APPROVAL_WORKFLOW_LABEL } from './approvalWorkflow';
import { LEAVE_NON_WORKING_REASON_LABEL } from './nonWorkingReasons';

// ─────────────────────────── Vocaboli (valori BE reali) ───────────────────────────

export const LEAVE_STATUSES = ['draft', 'submitted', 'manager_approved', 'approved', 'rejected', 'cancelled', 'expired'] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];

export const STATUS_LABEL: Record<LeaveStatus, string> = {
  draft: 'Bozza',
  submitted: 'Inviata',
  manager_approved: 'Appr. responsabile',
  approved: 'Approvata',
  rejected: 'Rifiutata',
  cancelled: 'Annullata',
  expired: 'Scaduta',
};
// Palette MaeDesign: solo azzurro (attesa/info) · green (ok) · red (ko) · gray (neutro) · yellow (avviso).
export const STATUS_COLOR: Record<LeaveStatus, string> = {
  draft: 'gray',
  submitted: 'azzurro',
  manager_approved: 'azzurro',
  approved: 'green',
  rejected: 'red',
  cancelled: 'gray',
  expired: 'yellow',
};

export const UNIT_OF_MEASURE = ['days', 'hours', 'either'] as const;
export const ACCRUAL_POLICIES = ['none', 'monthly', 'yearly', 'seniority-based', 'ccnl-based', 'per-event'] as const;
export const CARRY_OVER_POLICIES = ['none', 'unlimited', 'expiring'] as const;
// Categoria di raggruppamento del tipo di assenza (enum BE v1.49 — validato server-side)
export const LEAVE_CATEGORIES = ['vacation', 'permit', 'sickness', 'other'] as const;
export const LEAVE_CATEGORY_LABEL: Record<string, string> = {
  vacation: 'Ferie', permit: 'Permesso', sickness: 'Malattia', other: 'Altro',
};
// Eleggibilità del tipo (BE v1.66 P3): `all` = balance eager per tutti · `individual` = solo
// se HR abilita il singolo dipendente creando il balance (L104/CPAR: verbale ASL, figli).
export const LEAVE_ELIGIBILITIES = ['all', 'individual'] as const;
export const LEAVE_ELIGIBILITY_LABEL: Record<string, string> = {
  all: 'Automatico per tutti', individual: 'Solo su abilitazione HR',
};
export const DOCUMENT_TYPES = ['none', 'medical-certificate', 'inps-notification', 'union-authorization', 'court-order', 'other'] as const;
export const DOCUMENT_SUBMISSION_MODES = ['code_only', 'attachment_only', 'code_or_attachment', 'both'] as const;
export const AUTO_APPROVE_POLICIES = ['never', 'with-document', 'with-substitute', 'always'] as const;
export const ATTACHMENT_TYPES = ['medical-certificate', 'inps-notification', 'union-authorization', 'court-order', 'other'] as const;
export const BLACKOUT_ENFORCEMENTS = ['block', 'warn'] as const;
export type LeaveEventAction = 'submitted' | 'manager_approved' | 'admin_approved' | 'rejected' | 'cancelled' | 'reopened' | 'auto_approved' | 'expired';

// ─────────────────────────── Tipi risorse JSON:API ───────────────────────────

type Ref = { id: string; type: string };
/* `user` c'e' solo con `include: ['employee.user']`: serve a risalire alla foto profilo
   (`user-profiles.profileImage` e' agganciato all'utente, non al dipendente). */
type EmployeeRef = Ref & { firstName?: string; lastName?: string; email?: string; user?: { id: string } | null };

export interface LeaveRequestRow extends Denormalized {
  type: 'leave-requests';
  id: string;
  /** Task 1921: generata da una chiusura aziendale. Non si tocca da sola: si cambia la chiusura. */
  companyClosure?: { id: string; name?: string } | null;
  description?: string;
  certificateCode?: string;
  fromDate?: string;
  toDate?: string;
  status?: LeaveStatus;            // view-only (cambia solo via workflow)
  unitOfMeasure?: string;
  partOfDay?: string;
  startTime?: string;
  endTime?: string;
  durationValue?: number;
  /**
   * DURATION-AWARE (BE v1.78): ore effettive equivalenti, riscritte a **ogni** ricalcolo —
   * anche in bozza e anche sulle richieste contate a giornate. Read-only.
   * Serve a spiegare perché cinque giorni possono valere 36 ore e non 40: part-time,
   * turni corti, pausa. Il `durationValue` da solo non lo può dire.
   */
  durationHoursEquivalent?: number | null;
  handoverNotes?: string;
  priority?: string;
  isRetroactive?: boolean;
  /**
   * BE v1.71 (WORKING-DAYS-CALC §2): la durata è **provvisoria** perché la turnazione non è
   * pubblicata per tutto il periodo. Viene ricalcolata alla pubblicazione dei turni → va
   * mostrata all'utente, non nascosta.
   */
  isProvisional?: boolean;
  submittedAt?: string;
  // ── DOCUMENT-GRACE (BE v1.77): obbligo documentale con termine post-submit ──
  // Tutti read-only: li scrive il server (submit, job giornaliero, endpoint di deroga).
  /** Termine di consegna; `null` = nessun obbligo pendente. NON viene azzerato alla consegna. */
  documentDueDate?: string | null;
  /** Quando il job ha rilevato il ritardo. Resta valorizzato dopo la consegna tardiva: traccia storica. */
  documentOverdueAt?: string | null;
  /** Il payroll non deve conteggiare l'assenza. Persistito dal server. */
  payrollBlocked?: boolean;
  documentWaiver?: boolean;
  documentWaiverReason?: string | null;
  managerApprovedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  cancelledAt?: string;
  rejectionReason?: string;
  cancellationReason?: string;
  calendarEventId?: string;
  createdAt?: string;
  updatedAt?: string;
  employee?: EmployeeRef | null;
  validatorEmployee?: EmployeeRef | null;
  substituteEmployee?: EmployeeRef | null;
  leaveType?: (LeaveTypeRow | Ref) | null;
  leaveReason?: (LeaveReasonRow | Ref) | null;
}

export interface LeaveTypeRow extends Denormalized {
  type: 'leave-types';
  id: string;
  code?: string;
  name?: string;
  description?: string;
  unitOfMeasure?: string;
  accrualPolicy?: string;
  entitledDaysPerYear?: number;
  entitledDaysPerEvent?: number;
  maxEventsPerYear?: number;
  carryOverPolicy?: string;
  minAdvanceDays?: number;
  maxConsecutiveDays?: number;
  requiresDocument?: string;
  documentRequiredAfterDays?: number;
  documentSubmissionMode?: string;
  requiresSubstitute?: boolean;
  substituteRequiredAfterDays?: number;
  requiresHandover?: boolean;
  /** Soglia handover per-tipo (BE v1.70): obbligatorio oltre N giorni. `requiresHandover` prevale. */
  handoverRequiredAfterDays?: number | null;
  autoApprovePolicy?: string;
  impactsPayroll?: boolean;
  paidLeave?: boolean;
  isRejectable?: boolean;
  requiresReason?: boolean;
  color?: string;
  icon?: string;
  sortOrder?: number;
  isActive?: boolean;
  scope?: string;
  category?: 'vacation' | 'permit' | 'sickness' | 'other';
  /** `all` (default) | `individual` — vedi `LEAVE_ELIGIBILITIES`. Non impatta l'approvazione. */
  eligibility?: string;
  /** BE v1.71: il tipo ammette la cancellazione di singoli giorni su richiesta approvata. */
  allowsPartialCancellation?: boolean;
  /**
   * BE v1.72 (PROTECTED-STATUS §1.3) — chi può **richiedere** il tipo:
   * `open` (tutti, default) · `restricted` (whitelist manuale) · `protected-status`
   * (serve uno status protetto attivo fra `requiredProtectedStatusTypes`).
   * Asse diverso da `eligibility`, che riguarda solo la creazione del bilancio.
   */
  eligibilityMode?: string;
  /** CSV di statusType richiesti quando `eligibilityMode='protected-status'`. */
  requiredProtectedStatusTypes?: string;
  /** CSV opzionale di subType, per restringere ai casi gravi. */
  requiredProtectedStatusSubTypes?: string;
  /**
   * APPROVAL-WORKFLOW-CONFIG (BE v1.76): `manager-only` | `two-level` | `auto`.
   * `null` = eredita dal tenant. Il BE espone **null** (non `''`) quando eredita: la UI
   * distingue "eredita" da "override esplicito" esattamente su questo.
   */
  approvalWorkflow?: string | null;
}

/** Frazione di giornata in forma breve (celle e liste compatte). */
export const PART_OF_DAY_LABEL_SHORT: Record<string, string> = {
  'full-day': 'Intera', morning: 'Mattina', afternoon: 'Pomeriggio',
};

export const LEAVE_ELIGIBILITY_MODES = ['open', 'restricted', 'protected-status'] as const;
export const LEAVE_ELIGIBILITY_MODE_LABEL: Record<string, string> = {
  open: 'Aperto a tutti',
  restricted: 'Whitelist manuale',
  'protected-status': 'Richiede status protetto',
};

/** Il tipo non è liberamente richiedibile: serve una whitelist o uno status protetto. */
export const isTypeGated = (t?: LeaveTypeRow | null): boolean =>
  Boolean(t?.eligibilityMode && t.eligibilityMode !== 'open');

/** Riga di `leave-request-days` (BE v1.71): granularità per-giorno della richiesta. */
export interface LeaveRequestDayRow extends Denormalized {
  type: 'leave-request-days';
  id: string;
  date?: string;
  partOfDay?: string;
  startTime?: string;
  endTime?: string;
  durationValue?: number;
  /** `active` | `cancelled`. */
  status?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  leaveRequest?: Ref | null;
  cancelledByEmployee?: EmployeeRef | null;
}

/**
 * Tipo **senza bilancio annuale** (BE v1.66 P2): non riceve un `leave-balance` eager, quindi
 * non va mostrato con un residuo — il consumo si legge dalle richieste approvate.
 *
 * La regola del BE è «documento sanitario/INPS **e** nessun accrual». Copre malattia e
 * infortunio, ma dal seed v1.74 anche **maternità e congedo parentale**: sono pagati da INPS
 * su date certificate, non da un budget aziendale. Per questo il nome parla di bilancio e non
 * di malattia: etichettarli come sanitari sarebbe sbagliato in UI.
 */
export function hasNoAnnualBudget(t?: LeaveTypeRow | null): boolean {
  if (!t) return false;
  if (t.category === 'sickness') return true;
  return t.accrualPolicy === 'none'
    && (t.requiresDocument === 'medical-certificate' || t.requiresDocument === 'inps-notification');
}

/** @deprecated Nome fuorviante da quando la regola copre anche maternità/congedi: usa `hasNoAnnualBudget`. */
export const isSicknessType = hasNoAnnualBudget;

/** Il tipo ha un bilancio annuale mostrabile. */
export function typeHasBudget(t?: LeaveTypeRow | null): boolean {
  return Boolean(t) && !hasNoAnnualBudget(t);
}

/** Richiesta a cavallo d'anno: il BE ripartisce il consumo sui due bilanci (P4b). */
export function isCrossYear(fromDate?: string, toDate?: string): boolean {
  return Boolean(fromDate && toDate && fromDate.slice(0, 4) !== toDate.slice(0, 4));
}

export interface LeaveReasonRow extends Denormalized {
  type: 'leave-reasons';
  id: string;
  code?: string;
  name?: string;
  description?: string;
  isRejectable?: boolean;
  isHighlighted?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  scope?: string;
  leaveType?: Ref | null;
}

export interface LeaveBalanceRow extends Denormalized {
  type: 'leave-balances';   // READ-ONLY (403 in scrittura)
  id: string;
  year?: number;
  periodStart?: string;
  periodEnd?: string;
  entitledDays?: number;
  accruedDays?: number;
  usedDays?: number;
  pendingDays?: number;
  carryOverDays?: number;
  carryOverExpiresAt?: string;
  availableDays?: number;   // computed
  employee?: EmployeeRef | null;
  leaveType?: (LeaveTypeRow | Ref) | null;
}

export interface LeaveBlackoutRow extends Denormalized {
  type: 'leave-blackout-periods';
  id: string;
  name?: string;
  fromDate?: string;
  toDate?: string;
  description?: string;
  enforcement?: string;
  allowEmergencies?: boolean;
  scope?: string;
  affectsAllTypes?: boolean;
  isActive?: boolean;
}

export interface LeaveAttachmentRow extends Denormalized {
  type: 'leave-request-attachments';
  id: string;
  attachmentType?: string;
  uploadedAt?: string;
  /** Riferimento per il grace period di cancellazione (il BE misura su `createdAt`). */
  createdAt?: string;
  leaveRequest?: Ref | null;
  mediaFile?: Ref | null;
  uploadedByEmployee?: EmployeeRef | null;
}

export const ATTACHMENT_TYPE_LABEL: Record<string, string> = {
  'medical-certificate': 'Certificato medico',
  'inps-notification': 'Comunicazione INPS',
  'union-authorization': 'Autorizzazione sindacale',
  'court-order': 'Ordine tribunale',
  other: 'Altro',
};

export interface LeaveCommentRow extends Denormalized {
  type: 'leave-request-comments';
  id: string;
  body?: string;
  isInternal?: boolean;
  createdAt?: string;
  authorEmployee?: EmployeeRef | null;
}

export interface LeaveEventRow extends Denormalized {
  type: 'leave-request-events';   // READ-ONLY
  id: string;
  action?: LeaveEventAction;
  actorRole?: 'employee' | 'manager' | 'admin' | 'system';
  note?: string;
  createdAt?: string;
  actorEmployee?: EmployeeRef | null;
}

// ─────────────────────────── Workflow (state-machine) ───────────────────────────

const T = 'leave-requests';

export const submitLeave = (id: string, note?: string): Promise<WorkflowResult> =>
  runWorkflowAction(T, id, 'submit', note ? { note } : {});
export const managerApproveLeave = (id: string, note?: string): Promise<WorkflowResult> =>
  runWorkflowAction(T, id, 'manager-approve', note ? { note } : {});
export const adminApproveLeave = (id: string, note?: string): Promise<WorkflowResult> =>
  runWorkflowAction(T, id, 'admin-approve', note ? { note } : {});
export const rejectLeave = (id: string, rejectionReason: string, note?: string): Promise<WorkflowResult> =>
  runWorkflowAction(T, id, 'reject', { rejectionReason, ...(note ? { note } : {}) });
export const cancelLeave = (id: string, cancellationReason?: string, note?: string): Promise<WorkflowResult> =>
  runWorkflowAction(T, id, 'cancel', { ...(cancellationReason ? { cancellationReason } : {}), ...(note ? { note } : {}) });
export const reopenLeave = (id: string, note?: string): Promise<WorkflowResult> =>
  runWorkflowAction(T, id, 'reopen', note ? { note } : {});

// ───────────── Stima durata — DURATION-AWARE (BE v1.78–v1.80.1, era v1.54) ─────────────

/**
 * Da v1.78 la durata non guarda più solo il calendario: passa per turno assegnato,
 * rotazione, orario personale, orario di sede e pausa. Il `breakdown` per giorno racconta
 * quel calcolo, ed è l'unico modo che ha l'utente di capire perché cinque giorni di
 * richiesta valgono 4,5 sul bilancio.
 *
 * `reason` **non vale più `'working-day'`** sui giorni lavorativi (v1.79): dice da dove
 * viene l'orario. Noi ci basiamo su `isWorkingDay`, quindi il cambio non ci ha toccati —
 * resta scritto qui perché è la trappola in cui cade chi confronta la stringa.
 */
export type LeaveDurationReason =
  | 'shift-scheduled' | 'rotation-work' | 'rotation-rest' | 'working-hour' | 'working-hour-off'
  | 'site-working-time' | 'fallback' | 'weekend' | 'holiday' | 'patron-saint' | 'shift-rest'
  | 'out-of-service' | (string & {});

/** Anomalie che il BE segnala sul singolo giorno (§2 del contratto DURATION-AWARE). */
export type LeaveDurationWarning =
  | 'OUT_OF_SHIFT_PARTIAL' | 'OUT_OF_SHIFT_FULL' | 'BREAK_NOT_APPLIED' | 'PLANNING_MISSING'
  | 'HOLIDAY_OVERRIDE' | 'CONTRACT_EXPIRED_BEFORE_DAY' | 'SHIFT_CANCELLED' | (string & {});

export interface LeaveDurationBreakdownDay {
  date: string;
  isWorkingDay: boolean;
  reason?: LeaveDurationReason;
  /** Finestra di **presenza** (lorda): la pausa è a fianco in `breakMinutes`, non già tolta. */
  workingIntervals?: { start: string; end: string }[];
  breakMinutes?: number;
  requestedIntervals?: { start: string; end: string }[];
  overlapMinutes?: number;
  /** `overlap − pausa`: i minuti che finiscono davvero sul bilancio. */
  netMinutes?: number;
  warnings?: LeaveDurationWarning[];
}

/** Copertura del planning turni **sul range richiesto** (semantica corretta in v1.80.1). */
export type LeavePlanningCoverage = 'full' | 'partial' | 'none';

export interface LeaveDurationEstimate {
  durationValue: number;
  durationUnit?: string;
  /** Ore effettive equivalenti, valorizzate anche sulle richieste a giornate. */
  durationHoursEquivalent?: number | null;
  /** `true` = turnazione non ancora pubblicata su parte del range: la durata cambierà. */
  isProvisional?: boolean;
  planningCoverage?: LeavePlanningCoverage | null;
  planningCoverageEndDate?: string | null;
  breakdown: LeaveDurationBreakdownDay[];
}

/**
 * Stima autorevole della durata: è lo **stesso** calcolo del `/submit`, quindi il numero
 * mostrato nel form è quello che finirà sul record (invariante verificata dai test BE).
 *
 * Envelope custom `{ data: {...}, meta: { durationCalculation: {...} } }`. Fino a v1.80.0
 * `planningCoverage` viveva **solo** in `meta.durationCalculation` e chi lo leggeva da
 * `data` trovava `undefined`; da v1.80.1 c'è in entrambi. Leggiamo `data` con fallback su
 * `meta` per restare compatibili con un BE non ancora aggiornato.
 */
export async function estimateLeaveDuration(params: {
  employeeId: string; fromDate: string; toDate: string;
  partOfDay?: string; unitOfMeasure?: string; startTime?: string; endTime?: string;
}): Promise<LeaveDurationEstimate> {
  const qs = new URLSearchParams({ employeeId: params.employeeId, fromDate: params.fromDate, toDate: params.toDate });
  if (params.partOfDay) qs.set('partOfDay', params.partOfDay);
  if (params.unitOfMeasure) qs.set('unitOfMeasure', params.unitOfMeasure);
  if (params.startTime) qs.set('startTime', params.startTime);
  if (params.endTime) qs.set('endTime', params.endTime);

  type Envelope = { data?: Partial<LeaveDurationEstimate>; meta?: { durationCalculation?: Partial<LeaveDurationEstimate> } }
    & Partial<LeaveDurationEstimate>;
  const res = await httpRequest<Envelope>(
    `/api/leave-requests/estimate-duration?${qs.toString()}`, { endpointType: 'plain', retries: 0 });

  const d = (res && typeof res === 'object' && 'data' in res && res.data) ? res.data : (res as Partial<LeaveDurationEstimate>);
  const meta = (res && typeof res === 'object' ? res.meta?.durationCalculation : undefined) ?? {};

  return {
    durationValue: Number(d?.durationValue ?? 0),
    durationUnit: d?.durationUnit ?? meta.durationUnit,
    durationHoursEquivalent: d?.durationHoursEquivalent ?? meta.durationHoursEquivalent ?? null,
    isProvisional: d?.isProvisional ?? meta.isProvisional ?? false,
    planningCoverage: d?.planningCoverage ?? meta.planningCoverage ?? null,
    planningCoverageEndDate: d?.planningCoverageEndDate ?? meta.planningCoverageEndDate ?? null,
    breakdown: d?.breakdown ?? meta.breakdown ?? [],
  };
}

/* Le etichette stanno in `nonWorkingReasons.ts`: le legge anche l'applicazione di un modello di
   rendicontazione, che non deve portarsi dietro questo file. */
export { LEAVE_NON_WORKING_REASON_LABEL };

/**
 * Riepilogo leggibile dei giorni esclusi, per motivo: «2 riposi da turno, 1 festività».
 * Stringa vuota se non c'è nulla da escludere.
 */
export function summarizeExcludedDays(breakdown: LeaveDurationBreakdownDay[]): string {
  const byReason = new Map<string, number>();
  for (const d of breakdown) {
    if (d.isWorkingDay) continue;
    const label = LEAVE_NON_WORKING_REASON_LABEL[d.reason ?? ''] ?? 'giorno non lavorativo';
    byReason.set(label, (byReason.get(label) ?? 0) + 1);
  }
  if (byReason.size === 0) return '';
  return [...byReason.entries()]
    .map(([label, n]) => `${n} ${n === 1 ? label : pluralize(label)}`)
    .join(', ');
}

/** Plurali dei pochi sostantivi che usiamo — niente libreria per sette parole. */
function pluralize(label: string): string {
  if (label.endsWith('à')) return label;                    // festività
  if (label.startsWith('riposo')) return label.replace('riposo', 'riposi');
  if (label === 'weekend' || label === 'patrono') return label === 'patrono' ? 'patroni' : label;
  if (label.startsWith('giorno')) return label.replace('giorno', 'giorni');
  if (label === 'chiusura aziendale') return 'chiusure aziendali';
  return label;
}

/** Etichette dei warning per giorno: cosa è successo e, dove serve, cosa comporta. */
export const LEAVE_DURATION_WARNING_LABEL: Record<string, string> = {
  OUT_OF_SHIFT_PARTIAL: 'Parte dell’orario richiesto cade fuori dal turno: contano solo le ore dentro il turno.',
  OUT_OF_SHIFT_FULL: 'L’orario richiesto è tutto fuori dal turno: quel giorno non scala nulla dal bilancio.',
  BREAK_NOT_APPLIED: 'La pausa non è stata sottratta: per quel giorno non risulta configurata.',
  PLANNING_MISSING: 'La turnazione non è ancora pubblicata: la durata di quel giorno è una stima.',
  HOLIDAY_OVERRIDE: 'Giorno festivo: vince la festività sul turno previsto.',
  CONTRACT_EXPIRED_BEFORE_DAY: 'Il rapporto di lavoro risulta chiuso prima di quel giorno.',
  SHIFT_CANCELLED: 'Il turno di quel giorno è stato annullato: l’orario viene dal tuo orario di lavoro o dalla sede.',
};

/** Raggruppa i warning del breakdown per codice, con i giorni coinvolti. */
export function groupDurationWarnings(
  breakdown: LeaveDurationBreakdownDay[],
): { code: string; label: string; dates: string[] }[] {
  const byCode = new Map<string, string[]>();
  for (const day of breakdown) {
    for (const w of day.warnings ?? []) {
      const list = byCode.get(w) ?? [];
      list.push(day.date);
      byCode.set(w, list);
    }
  }
  return [...byCode.entries()].map(([code, dates]) => ({
    code,
    // Un codice non mappato non si nasconde: si mostra grezzo, così si vede che è da tradurre.
    label: LEAVE_DURATION_WARNING_LABEL[code] ?? code,
    dates,
  }));
}

// ─────────────────────────── Giorni: cancellazione parziale (BE v1.71) ───────────────────────────

export interface CancelDaysResult {
  cancelledDaysCount: number;
  remainingActiveDaysCount: number;
  newDurationValue: number;
  /** Il BE annulla l'intera richiesta quando non resta nessun giorno attivo. */
  autoCancelled: boolean;
  warnings: WorkflowWarning[];
}

/**
 * Cancella singoli giorni di una richiesta **approvata** (`POST /{id}/cancel-days`).
 *
 * Vincoli del BE: motivazione obbligatoria · solo su `approved` · solo sui tipi con
 * `allowsPartialCancellation` · nessun giorno già passato. I giorni non trovati o già
 * cancellati non bloccano: tornano in `meta.warnings`. Se non resta nessun giorno attivo
 * la richiesta passa a `cancelled`.
 */
export async function cancelLeaveDays(
  id: string,
  args: { dates?: string[]; dayIds?: string[]; reason: string },
): Promise<CancelDaysResult> {
  const body: Record<string, unknown> = { reason: args.reason };
  if (args.dates?.length) body.dates = args.dates;
  else if (args.dayIds?.length) body.dayIds = args.dayIds;
  const res = await runWorkflowAction(T, id, 'cancel-days', body);
  const n = (k: string) => Number(res.meta[k] ?? 0);
  return {
    cancelledDaysCount: n('cancelledDaysCount'),
    remainingActiveDaysCount: n('remainingActiveDaysCount'),
    newDurationValue: n('newDurationValue'),
    autoCancelled: res.transitionedTo === 'cancelled',
    warnings: res.warnings,
  };
}

/** Giorni di una richiesta, ordinati. */
export const fetchLeaveRequestDays = (leaveRequestId: string) =>
  fetchList<LeaveRequestDayRow>('leave-request-days', {
    filter: { 'leaveRequest.id': { eq: leaveRequestId } },
    include: ['cancelledByEmployee'],
    sort: ['date'],
    page: { size: 100 },
  });

export interface TeamOverlapEntry {
  employeeId?: string;
  employeeName?: string;
  leaveRequestId?: string;
  leaveTypeName?: string;
  partOfDay?: string;
  [k: string]: unknown;
}

export interface TeamOverlap {
  date: string;
  entries: TeamOverlapEntry[];
  /** Quante persone del perimetro sono assenti in quella data. */
  totalCount: number;
  /** Soglia oltre la quale il BE emette il warning soft (`leave.overlapWarningThreshold`). */
  warningThreshold: number;
  exceedsThreshold: boolean;
}

/**
 * Sovrapposizioni del team in una data (`GET /api/leave-request-days/team-overlap`).
 *
 * La soglia è **la stessa** del warning soft al submit, così l'avviso che vede l'approvatore
 * e quello che ha visto il richiedente non si contraddicono. Forma reale della risposta:
 * `{ data: [...], meta: { totalCount, warningThreshold, exceedsThreshold } }`.
 */
export async function fetchLeaveTeamOverlap(date: string, approverEmployeeId?: string): Promise<TeamOverlap> {
  const qs = new URLSearchParams({ date });
  if (approverEmployeeId) qs.set('approverEmployeeId', approverEmployeeId);
  const res = await httpRequest<{
    data?: TeamOverlapEntry[];
    meta?: { totalCount?: number; warningThreshold?: number; exceedsThreshold?: boolean };
  }>(`/api/leave-request-days/team-overlap?${qs.toString()}`, { endpointType: 'plain', retries: 0 });
  const m = res?.meta ?? {};
  return {
    date,
    entries: Array.isArray(res?.data) ? res.data : [],
    totalCount: Number(m.totalCount ?? 0),
    warningThreshold: Number(m.warningThreshold ?? 0),
    exceedsThreshold: Boolean(m.exceedsThreshold),
  };
}

/**
 * Tipi che il dipendente corrente può effettivamente richiedere
 * (`GET /api/leave-types/eligible-for-me`): esclude i tipi `restricted` senza whitelist e
 * quelli che richiedono uno status protetto non attivo. Il detail d'errore del BE è neutro
 * per non rivelare dati sanitari, quindi filtrare a monte evita di far sbattere l'utente
 * contro un 422 che non può spiegargli il motivo.
 */
export const fetchEligibleLeaveTypes = () =>
  httpRequest<unknown>('/api/leave-types/eligible-for-me', { endpointType: 'plain', retries: 0 });

// ─────────────────────────── Calendario del team (task 2057) ───────────────────────────

/**
 * Una giornata (o un tratto di giornate) occupata da un collega, come la dà
 * `GET /api/team-calendar`: la lettura **ridotta** che ogni dipendente può fare su tutta l'azienda.
 *
 * Il tipo dell'assenza (`leaveTypeId`/`leaveTypeName`) e `requestId` arrivano solo a chi la richiesta
 * la potrebbe già leggere — HR, chi tratta dati sensibili, sé stessi e il proprio organigramma sotto —
 * e sono `null` per gli altri: è il sistema a tenere per sé una malattia, non lo schermo.
 */
export interface TeamCalendarEntry {
  employeeId: string;
  /** `YYYY-MM-DD`, già tagliati sulla finestra chiesta. */
  from: string;
  to: string;
  /** Il vocabolario delle richieste: `full-day`, non `full`. */
  partOfDay: 'full-day' | 'morning' | 'afternoon' | 'hours' | string;
  startTime?: string | null;
  endTime?: string | null;
  /** `absence` è tutto ciò che non è ferie; `office` il rientro di chi è in smart di default. */
  kind: 'vacation' | 'absence' | 'smart' | 'office' | string;
  /** `submitted`/`manager_approved`; bozze, rifiutate, annullate e scadute non escono. */
  pending: boolean;
  leaveTypeId: string | null;
  leaveTypeName: string | null;
  requestId: string | null;
}

/**
 * Finestra al massimo di 93 giorni: un mese ci sta sempre. Gruppo e sede restringono a chi ne fa
 * parte, e si possono dare insieme.
 */
export const fetchTeamCalendar = async (
  from: string, to: string, scope: { businessGroupId?: string | null; siteId?: string | null } = {},
): Promise<TeamCalendarEntry[]> => {
  const qs = new URLSearchParams({ from, to });
  if (scope.businessGroupId) qs.set('businessGroupId', scope.businessGroupId);
  if (scope.siteId) qs.set('siteId', scope.siteId);
  const body = await httpRequest<TeamCalendarEntry[] | { data?: TeamCalendarEntry[] }>(
    `/api/team-calendar?${qs.toString()}`, { endpointType: 'plain' },
  );
  return Array.isArray(body) ? body : (body?.data ?? []);
};

// ─────────────────────────── Allegati: grace period (BE v1.64) ───────────────────────────

/**
 * DELETE di un allegato. Il BE valuta in ordine: richiesta non approvata → libero ·
 * autore entro `leave.attachmentGraceMinutes` (default 30′) → consentito ·
 * HR admin **con motivazione obbligatoria** (`?reason=`, min 5 caratteri) →
 * consentito · altrimenti 409 `LEAVE_ATTACHMENT_LOCKED` con `meta.reason`
 * (`grace-expired` | `not-owner`).
 */
export async function deleteLeaveAttachment(id: string, reason?: string): Promise<void> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  await httpRequest(`/api/leave-request-attachments/${encodeURIComponent(id)}${qs}`, {
    method: 'DELETE', endpointType: 'plain', retries: 0,
  });
}

/** Minuti di grace residui sull'allegato (0 = scaduto). */
export function attachmentGraceLeft(createdAt: string | undefined, graceMinutes: number): number {
  if (!createdAt) return 0;
  const elapsed = (Date.now() - new Date(createdAt).getTime()) / 60_000;
  return Math.max(0, Math.ceil(graceMinutes - elapsed));
}

/**
 * Abilita un dipendente su un tipo a eleggibilità individuale creando il balance.
 * `POST /api/leave-balances` è consentito ai soli HR e solo per `eligibility='individual'`
 * (`LeaveResourceDefinition`; fuori da quel caso → 403 `LEAVE_BALANCE_READ_ONLY`).
 *
 * ⚠ **Nessun attributo va inviato.** Sull'entità `LeaveBalance` sono tutti `AllowView`,
 * `year` compreso: mandarlo fa fallire la deserializzazione con
 * *422 «The attribute 'year' … cannot be assigned to»* prima ancora di arrivare al
 * controller. Il server valorizza `year` (= anno corrente UTC quando ≤ 0),
 * `entitledDays`, `accruedDays` e il periodo di maturazione.
 *
 * Conseguenza per la UI: **il saldo nasce sempre sull'anno corrente**, non su quello
 * eventualmente selezionato nella card → l'azione va offerta solo sull'anno corrente
 * (vedi `LeaveBalancesCard`), altrimenti si creerebbe un saldo su un anno diverso da
 * quello che l'utente sta guardando, senza che nulla lo segnali.
 */
export const createIndividualBalance = (employeeId: string, leaveTypeId: string) =>
  createOne('leave-balances', {
    relationships: {
      employee: { data: { type: 'erp-employees', id: employeeId } },
      leaveType: { data: { type: 'leave-types', id: leaveTypeId } },
    },
  });

/**
 * Deroga all'obbligo documentale (DOCUMENT-GRACE §2): sblocca il payroll senza il
 * documento. Claim **dedicato** `leave.document.waive`, un gradino sopra l'essere HR.
 * `reason` >= 20 caratteri, validati anche lato server (422
 * LEAVE_DOCUMENT_WAIVER_REASON_REQUIRED): qui si valida prima per non far scrivere
 * l'utente a vuoto.
 */
export const waiveLeaveDocument = (id: string, reason: string): Promise<WorkflowResult> =>
  runWorkflowAction(T, id, 'document-waiver', { reason: reason.trim() });

/** Documento completo dopo una transizione (l'envelope workflow è parziale). */
export const refetchLeaveRequest = (id: string): Promise<LeaveRequestRow | null> =>
  fetchOne<LeaveRequestRow>('leave-requests', id, { include: ['leaveType', 'leaveReason', 'employee', 'validatorEmployee', 'substituteEmployee'] });

/** Transizioni ammesse dallo stato corrente (mirror BE), per costruire i bottoni azione. */
export function allowedLeaveTransitions(status: LeaveStatus, opts: { isManager: boolean; isAdmin: boolean; isOwner: boolean }): LeaveAction[] {
  const out: LeaveAction[] = [];
  if (status === 'draft' && opts.isOwner) out.push('submit', 'cancel');
  if (status === 'submitted') {
    if (opts.isManager || opts.isAdmin) out.push('manager-approve', 'reject');
    if (opts.isOwner) out.push('cancel');
  }
  if (status === 'manager_approved') {
    if (opts.isAdmin) out.push('admin-approve', 'reject');
    if (opts.isOwner) out.push('cancel');
  }
  if (status === 'approved' && opts.isOwner) out.push('cancel');
  if ((status === 'rejected' || status === 'cancelled') && opts.isAdmin) out.push('reopen');
  return out;
}
export type LeaveAction = 'submit' | 'manager-approve' | 'admin-approve' | 'reject' | 'cancel' | 'reopen';
export const ACTION_LABEL: Record<LeaveAction, string> = {
  submit: 'Invia', 'manager-approve': 'Approva (responsabile)', 'admin-approve': 'Approva (HR)',
  reject: 'Rifiuta', cancel: 'Annulla', reopen: 'Riapri',
};

// ─────────────────────────── Config tenant (leave.*) ───────────────────────────

export const LEAVE_CONFIG_DEFAULTS = {
  attachmentGraceMinutes: 30,
  retroactiveMaxDays: 7,
  overlapWarningThreshold: 3,
  overlapScope: 'site',
  handoverThresholdDays: 5,
  escalationHours: 48,
  expirationDays: 14,
  reopenWindowDays: 30,
  nonRejectableAutoApprove: true,
} as const;

// ─────────────────────────── Schemi admin (cataloghi) ───────────────────────────

export const UOM_LABEL: Record<string, string> = { days: 'Giorni', hours: 'Ore', either: 'Giorni o ore' };
export const ACCRUAL_LABEL: Record<string, string> = { none: 'Nessuna', monthly: 'Mensile', yearly: 'Annuale', 'seniority-based': 'Per anzianità', 'ccnl-based': 'Da CCNL', 'per-event': 'Per evento' };
export const CARRY_OVER_LABEL: Record<string, string> = { none: 'Nessuno', unlimited: 'Illimitato', expiring: 'Con scadenza' };
export const DOCUMENT_TYPE_LABEL: Record<string, string> = { none: 'Nessuno', 'medical-certificate': 'Certificato medico', 'inps-notification': 'Comunicazione INPS', 'union-authorization': 'Autorizzazione sindacale', 'court-order': 'Ordine tribunale', other: 'Altro' };
export const DOC_SUBMISSION_LABEL: Record<string, string> = { code_only: 'Solo codice', attachment_only: 'Solo allegato', code_or_attachment: 'Codice o allegato', both: 'Entrambi' };
export const AUTO_APPROVE_LABEL: Record<string, string> = { never: 'Mai', 'with-document': 'Con documento', 'with-substitute': 'Con sostituto', always: 'Sempre' };
const BLACKOUT_ENFORCEMENT_LABEL: Record<string, string> = { block: 'Blocca', warn: 'Avvisa' };

export const LEAVE_TYPE_SCHEMA: CrudSchema = {
  type: 'leave-types', singular: 'tipo di assenza', defaultSort: ['sortOrder'],
  // Le due strade di auto-approvazione precedono il workflow e lo rendono inerte su 8
  // tipi CCNL su 12: l'avviso è l'unico posto dove la contraddizione si vede.
  banner: (v) => createElement(LeaveAutoApproveNotice, { values: v }),
  columns: [{ key: 'code', label: 'Codice' }, { key: 'name', label: 'Nome' }, enumCol('unitOfMeasure', 'Unità', UOM_LABEL), boolCol('paidLeave', 'Retribuita'), boolCol('isActive', 'Attivo')],
  fields: [
    /*
     * ⚠️ Il codice del tipo il sistema lo legge, non solo lo mostra: FERIE è quello che consumano
     * le chiusure aziendali e su cui si misurano gli obblighi delle ferie, MAL/LUTTO/SANGUE si
     * possono chiedere a giorni già passati, e il seed dei tipi standard (`LeaveSeeder`) li
     * riconosce per codice — rinominato FERIE, il seed successivo ne crea un secondo.
     */
    { name: 'code', label: 'Codice', kind: 'text', required: true,
      hint: 'Codice tecnico (FERIE, PERM, MAL), unico in azienda. Dopo la creazione non si cambia più.',
      lockedOnEdit: 'Non si cambia dopo la creazione: il sistema riconosce il tipo da questo codice (per esempio le chiusure aziendali scalano le FERIE). Per un codice diverso, crea un nuovo tipo e spegni questo.' },
    { name: 'name', label: 'Nome', kind: 'text', required: true,
      hint: 'È il nome che il dipendente legge nella select «Tipo di assenza».' },
    { name: 'description', label: 'Descrizione', kind: 'textarea',
      hint: 'Non compare nel modulo di richiesta: serve a chi configura, per ricordare a cosa serve il tipo.' },
    { name: 'unitOfMeasure', label: 'Unità di misura', kind: 'select', options: UNIT_OF_MEASURE, optionLabels: UOM_LABEL,
      hint: 'A giornate ammette la mezza giornata; a ore il dipendente indica gli orari puntuali. «Entrambe» lascia scegliere a lui.' },
    { name: 'accrualPolicy', label: 'Maturazione', kind: 'select', options: ACCRUAL_POLICIES, optionLabels: ACCRUAL_LABEL,
      hint: 'Decide se esiste un monte da consumare. «Nessuna» = tipo senza bilancio (malattia, congedi INPS): si conta solo quanto è stato usato, senza residuo.' },
    { name: 'accrualRatePerMonth', label: 'Rateo/mese (se mensile)', kind: 'decimal', requiredWhen: (v) => v.accrualPolicy === 'monthly',
      hint: 'Giorni che maturano ogni mese. Vale solo con maturazione mensile; con le altre politiche viene ignorato.' },
    { name: 'entitledDaysPerYear', label: 'Giorni/anno', kind: 'number',
      hint: 'Il monte annuo spettante. Lasciato vuoto su un tipo con maturazione, il bilancio resta a zero e nessuno può richiederlo.' },
    { name: 'entitledDaysPerEvent', label: 'Giorni/evento', kind: 'number',
      hint: 'Tetto per singola richiesta (matrimonio 15, lutto 3). Obbligatorio con maturazione «per evento»: oltre il tetto il sistema rifiuta l’invio.' },
    { name: 'maxEventsPerYear', label: 'Max eventi/anno', kind: 'number',
      hint: 'Quante volte l’anno si può usare (matrimonio 1, donazione sangue 4). Vuoto = illimitato.' },
    { name: 'carryOverPolicy', label: 'Riporto residuo', kind: 'select', options: CARRY_OVER_POLICIES, optionLabels: CARRY_OVER_LABEL,
      hint: 'Cosa succede al residuo a fine anno: si perde, si riporta senza scadenza, oppure si riporta e scade dopo N mesi.' },
    { name: 'carryOverExpirationMonths', label: 'Mesi scadenza riporto (se a scadenza)', kind: 'number', requiredWhen: (v) => v.carryOverPolicy === 'expiring',
      hint: 'Mesi dall’inizio dell’anno entro cui il residuo riportato va consumato. Scaduto, un job lo azzera.' },
    { name: 'minAdvanceDays', label: 'Preavviso min (gg)', kind: 'number',
      hint: 'Giorni fra oggi e l’inizio dell’assenza. Sotto soglia il dipendente vede un avviso e può proseguire solo contrassegnando la richiesta come urgente.' },
    { name: 'maxConsecutiveDays', label: 'Max gg consecutivi', kind: 'number',
      hint: 'Durata massima di una singola richiesta. Diverso da «Giorni/evento»: questo limita la lunghezza, quello il consumo.' },
    { name: 'requiresDocument', label: 'Documento richiesto', kind: 'select', options: DOCUMENT_TYPES, optionLabels: DOCUMENT_TYPE_LABEL,
      hint: 'Per le ferie lascia «Nessuno»; per la malattia scegli «Certificato medico». Con «Nessuno» i due campi qui sotto vengono ignorati.' },
    { name: 'documentRequiredAfterDays', label: 'Documento dopo N giorni', kind: 'number',
      hint: 'Tolleranza: la richiesta parte senza documento e il dipendente ha N giorni dall’invio per consegnarlo (es. malattia dopo 3). Vuoto = obbligatorio subito.' },
    { name: 'documentSubmissionMode', label: 'Modalità documento', kind: 'select', options: DOCUMENT_SUBMISSION_MODES, optionLabels: DOC_SUBMISSION_LABEL,
      hint: 'Cosa accetta il modulo: solo il codice (protocollo INPS), solo il file, uno dei due, o entrambi. Ignorato se non è richiesto alcun documento.' },
    { name: 'autoApprovePolicy', label: 'Auto-approvazione', kind: 'select', options: AUTO_APPROVE_POLICIES, optionLabels: AUTO_APPROVE_LABEL,
      hint: 'Approva senza intervento umano quando la condizione è soddisfatta: al solo invio, oppure solo se il documento è allegato o il sostituto indicato. Precede il workflow qui sotto e lo rende inerte.' },
    // Accanto all'auto-approvazione perche' e' lo stesso tema: quanti umani servono.
    // Vuoto = eredita dal tenant (il BE tratta '' come null).
    {
      name: 'approvalWorkflow', label: 'Workflow di approvazione', kind: 'select',
      options: APPROVAL_WORKFLOWS, optionLabels: APPROVAL_WORKFLOW_LABEL,
      placeholder: 'Eredita dall’azienda',
      hint: 'Svuota il campo per tornare a ereditare. «Nessuna approvazione» vale solo per questo tipo.',
    },
    // Coppie flag+soglia in un controllo a tre stati: il BE fa prevalere il flag, quindi
    // "sempre + soglia" sarebbe una combinazione ingannevole (BE v1.70, UI-FINDING §3).
    {
      name: 'requiresSubstitute', label: 'Sostituto', kind: 'threshold',
      thresholdField: 'substituteRequiredAfterDays', thresholdLabel: 'Da N giorni',
      hint: 'Il collega che copre il lavoro di chi si assenta. Se è obbligatorio, il dipendente deve sceglierlo nella richiesta, altrimenti non può inviarla; il collega viene avvisato e riceve le consegne. «Da N giorni» lo chiede solo per le assenze di quella durata o più lunghe; sulle assenze a ore non scatta mai.',
    },
    {
      name: 'requiresHandover', label: 'Passaggio di consegne', kind: 'threshold',
      thresholdField: 'handoverRequiredAfterDays', thresholdLabel: 'Da N giorni',
      hint: 'Una nota scritta dal dipendente su cosa lascia in sospeso e a chi rivolgersi mentre è assente. Se è obbligatoria, la richiesta non si invia finché la nota è vuota. «Da N giorni» la chiede solo per le assenze di quella durata o più lunghe; sulle assenze a ore non scatta mai.',
    },
    { name: 'requiresReason', label: 'Richiede motivazione', kind: 'switch',
      hint: 'Se attivo, al dipendente compare la tendina delle motivazioni di questo tipo, che si scrivono nella scheda «Motivazioni» qui sopra. Senza motivazioni il dipendente non può inviare la richiesta.' },
    { name: 'paidLeave', label: 'Retribuita', kind: 'switch',
      hint: 'Spento = congedo non retribuito: non contribuisce al bilancio.' },
    { name: 'impactsPayroll', label: 'Impatta busta paga', kind: 'switch',
      hint: 'Marca il tipo per l’export verso le paghe. Non cambia nulla dentro questa applicazione: serve a chi elabora i cedolini.' },
    { name: 'isRejectable', label: 'Rifiutabile', kind: 'switch',
      hint: 'Spento, il tipo non si può rifiutare e la richiesta viene approvata appena inviata, se l’azienda approva da sola ciò che non si può respingere (impostazione predefinita). Si usa per i permessi dovuti per legge, come la L104. Vale anche per la singola motivazione: basta uno dei due spento.' },
    { name: 'category', label: 'Categoria (raggruppamento)', kind: 'select', options: LEAVE_CATEGORIES, optionLabels: LEAVE_CATEGORY_LABEL,
      hint: 'Solo raggruppamento visivo: decide sotto quale intestazione compare nella select del dipendente. Non influenza maturazione né approvazione.' },
    // BE v1.71/v1.72: chi può richiedere il tipo e se ammette la cancellazione per giorni.
    { name: 'eligibility', label: 'Bilancio', kind: 'select', fullWidth: true, options: LEAVE_ELIGIBILITIES, optionLabels: LEAVE_ELIGIBILITY_LABEL,
      hint: '«Tutti» crea il saldo per l’intera azienda. «Individuale» lo fa creare dall’HR sul singolo dipendente, per i tipi che dipendono da un verbale o dai figli (L104, congedo parentale).' },
    { name: 'eligibilityMode', label: 'Chi può richiederlo', kind: 'select', fullWidth: true, options: LEAVE_ELIGIBILITY_MODES, optionLabels: LEAVE_ELIGIBILITY_MODE_LABEL,
      hint: 'Decide a chi compare il tipo nella select. «Aperto» a tutti; «Ristretto» solo a una lista di dipendenti; «Status protetto» solo a chi ha attivo uno degli status indicati qui sotto — e senza indicarne almeno uno il tipo resta invisibile a tutti.' },
    // Il BE valida il CSV voce per voce e rifiuta `protected-status` senza status
    // (LEAVE_ELIGIBILITY_MODE_REQUIRES_STATUS_TYPES): un refuso qui spegnerebbe il tipo
    // per l'intera azienda, quindi si scelgono dal vocabolario e sono obbligatori.
    {
      name: 'requiredProtectedStatusTypes', label: 'Status protetti richiesti', kind: 'csvMulti',
      options: PROTECTED_STATUS_TYPES, optionLabels: PROTECTED_STATUS_TYPE_LABELS,
      requiredWhen: (v) => v.eligibilityMode === 'protected-status',
      hint: 'Almeno uno se il tipo richiede uno status protetto. Basta che il dipendente ne abbia uno attivo.',
    },
    {
      name: 'requiredProtectedStatusSubTypes', label: 'Sottotipi richiesti (opzionale)', kind: 'csvMulti',
      options: PROTECTED_SUB_TYPES, optionLabels: PROTECTED_SUB_TYPE_LABELS,
      hint: 'Restringe ai soli casi indicati, es. solo grave.',
    },
    { name: 'allowsPartialCancellation', label: 'Ammette cancellazione per giorni', kind: 'switch',
      hint: 'Se attivo, il dipendente può cancellare un singolo giorno di un’assenza approvata senza annullarla tutta. Da lasciare spento dove il documento copre un periodo continuativo (malattia, congedi).' },
    { name: 'icon', label: 'Icona', kind: 'icon', hint: 'Come il tipo si riconosce nell’elenco e nel calendario. La stessa icona la vede anche Lares.' },
    { name: 'color', label: 'Colore (hex)', kind: 'text', hint: 'Tinta con cui il tipo appare nel calendario, es. #2E7D32.' },
    { name: 'sortOrder', label: 'Ordinamento', kind: 'number', hint: 'Posizione nella select del dipendente: i numeri bassi vanno in alto.' },
    /* Task 1921: maternità, paternità e congedi nascono accesi (seed del BE). */
    { name: 'suspendsObligations', label: 'Sospende i solleciti', kind: 'switch',
      hint: 'Chi è assente con questo tipo non riceve solleciti (pianificazione, smart working, ferie, nota spese) finché dura l’assenza. Acceso di partenza su maternità, paternità e congedo parentale. Le altre assenze lunghe, non di ferie, li sospendono comunque dopo 20 giorni lavorativi.' },
    { name: 'isActive', label: 'Attivo', kind: 'switch', defaultValue: true,
      hint: 'Spento, sparisce dalle nuove richieste ma le assenze già registrate restano valide.' },
  ],
};

const PART_FROM_LABEL: Record<string, string> = { full: 'Tutto il giorno', afternoon: 'Dal pomeriggio' };
const PART_TO_LABEL: Record<string, string> = { full: 'Tutto il giorno', morning: 'Solo la mattina' };

/**
 * LE CHIUSURE AZIENDALI (task 1921, BE 08-10-2026 §8).
 *
 * Ogni chiusura dice se **scala dalle ferie**: sì, e il server crea per ogni persona una richiesta
 * di ferie approvata legata alla chiusura (il saldo può andare sotto zero, e l'ufficio del
 * personale riceve un avviso); no, e il giorno è non lavorativo per tutti, col nome della chiusura.
 * In tutti e due i casi zero ore attese, zero capacità, fuori dai solleciti. Le ferie già prese che
 * cadono in una chiusura senza scalo vengono restituite.
 *
 * Sta in `area.leave`: la governa il suo lucchetto. Senza sedi vale per tutta l'azienda (e con
 * «anche le aziende del gruppo» per le figlie); con le sedi solo lì, e non scende.
 */
export const COMPANY_CLOSURE_SCHEMA: CrudSchema = {
  type: 'company-closures', singular: 'chiusura', defaultSort: ['-fromDate'],
  columns: [
    { key: 'name', label: 'Nome' }, dateCol('fromDate', 'Dal'), dateCol('toDate', 'Al'),
    boolCol('deductsFromLeave', 'Scala dalle ferie'),
    { key: 'sites', label: 'Dove', render: (r) => {
      const sites = (r as { sites?: { name?: string }[] }).sites ?? [];
      return sites.length ? sites.map((x) => x.name).filter(Boolean).join(', ') : 'Tutta l’azienda';
    } },
  ],
  fields: [
    { name: 'name', label: 'Nome', kind: 'text', required: true,
      hint: 'Quello che le persone leggono nell’avviso e nel calendario, per esempio «Chiusura estiva».' },
    { name: 'fromDate', label: 'Dal', kind: 'date', required: true, hint: 'Primo giorno chiuso, incluso. Le ferie già chieste su quei giorni si ricalcolano.' },
    { name: 'toDate', label: 'Al', kind: 'date', required: true, hint: 'Ultimo giorno chiuso, incluso. Al massimo un anno dopo l’inizio.' },
    { name: 'fromPartOfDay', label: 'Il primo giorno', kind: 'select', defaultValue: 'full',
      options: ['full', 'afternoon'], optionLabels: PART_FROM_LABEL,
      hint: '«Dal pomeriggio» per la vigilia chiusa a metà: la mattina si lavora. Il confine è metà dell’orario del giorno.' },
    { name: 'toPartOfDay', label: 'L’ultimo giorno', kind: 'select', defaultValue: 'full',
      options: ['full', 'morning'], optionLabels: PART_TO_LABEL,
      hint: '«Solo la mattina»: il pomeriggio si lavora. Su un giorno solo non si combinano pomeriggio e mattina: per il giorno intero lascia «Tutto il giorno».' },
    { name: 'deductsFromLeave', label: 'Scala dalle ferie', kind: 'switch', defaultValue: false,
      hint: 'Acceso: ognuno riceve una richiesta di ferie approvata per i suoi giorni lavorativi, e il saldo si scala anche sotto zero. Spento: è un giorno non lavorativo per tutti e il saldo non si tocca.' },
    { name: 'leaveType', label: 'Tipo di assenza da scalare', kind: 'reference', refType: 'leave-types',
      visibleWhen: (v) => Boolean(v.deductsFromLeave),
      hint: 'Vuoto = le ferie dell’azienda (il tipo FERIE).' },
    { name: 'sites', label: 'Sedi', kind: 'referenceMany', refType: 'erp-sites',
      hint: 'Vuoto = tutta l’azienda. Con le sedi vale solo per chi lavora lì, e non scende alle aziende del gruppo.' },
    { name: 'excludedEmployees', label: 'Persone che lavorano lo stesso', kind: 'referenceMany', refType: 'erp-employees',
      hint: 'Il presidio: per loro la chiusura non vale.' },
    { name: 'scope', label: 'Dove vale', kind: 'select', required: true, defaultValue: 'tenant',
      options: ['tenant', 'group'], optionLabels: { tenant: 'Solo questa azienda', group: 'Anche le aziende del gruppo' },
      hint: 'Con «Anche le aziende del gruppo» la chiusura vale anche per le aziende sotto questa, se non ha sedi.' },
  ],
};

export const LEAVE_REASON_SCHEMA: CrudSchema = {
  type: 'leave-reasons', singular: 'motivazione', defaultSort: ['sortOrder'],
  // Gemello del banner sul tipo: spegnere «Rifiutabile» qui fa approvare la richiesta
  // all'invio, non solo respingere il rifiuto. Ed è una AND col tipo — basta uno dei due.
  banner: (v) => createElement(LeaveReasonRejectNotice, { values: v }),
  columns: [{ key: 'code', label: 'Codice' }, { key: 'name', label: 'Nome' }, { key: 'leaveType', label: 'Tipo', render: (r) => { const t = (r as { leaveType?: { name?: string; code?: string } }).leaveType; return t?.name || t?.code || '—'; } }, boolCol('isActive', 'Attivo')],
  fields: [
    /*
     * ⚠️ Il codice si sceglie una volta. Le richieste agganciano la motivazione per id, quindi
     * cambiarlo non rompe lo storico; ma il seed dei tipi standard (`LeaveSeeder`, che gira alla
     * creazione dell'azienda e a mano da `seed-all`) riconosce le motivazioni **per codice**:
     * rinominata RICOVERO, il seed successivo la ricrea, e il tipo ne ha due.
     */
    { name: 'code', label: 'Codice', kind: 'text', required: true,
      hint: 'Codice tecnico, unico all’interno del tipo di assenza. Dopo la creazione non si cambia più.',
      lockedOnEdit: 'Non si cambia dopo la creazione: è il nome con cui il sistema riconosce la motivazione. Per un codice diverso, creane una nuova e spegni questa.' },
    { name: 'name', label: 'Nome', kind: 'text', required: true,
      hint: 'Quello che il dipendente legge nella tendina «Motivazione». Si può cambiare quando vuoi.' },
    { name: 'description', label: 'Descrizione', kind: 'textarea',
      hint: 'A uso di chi configura: non compare nel modulo di richiesta.' },
    { name: 'leaveType', label: 'Tipo di assenza', kind: 'reference', refType: 'leave-types',
      hint: 'La motivazione compare solo su questo tipo, e solo se il tipo ha «Richiede motivazione» acceso.' },
    { name: 'isRejectable', label: 'Rifiutabile', kind: 'switch',
      hint: 'Acceso, il responsabile può approvare o rifiutare la richiesta. Spento, chi sceglie questa motivazione non può vedersela rifiutare, anche se il tipo di assenza lo permette. Si spegne per i casi che vanno sempre coperti, come un ricovero.' },
    { name: 'isHighlighted', label: 'In evidenza', kind: 'switch',
      hint: 'Mette un contrassegno accanto alla motivazione, per le voci che richiedono attenzione in fase di approvazione.' },
    { name: 'sortOrder', label: 'Ordinamento', kind: 'number',
      hint: 'Posizione nella tendina: i numeri bassi vanno in alto.' },
    { name: 'isActive', label: 'Attivo', kind: 'switch', defaultValue: true,
      hint: 'Spenta, sparisce dalle nuove richieste; quelle già inviate la conservano.' },
  ],
};

export const LEAVE_BLACKOUT_SCHEMA: CrudSchema = {
  type: 'leave-blackout-periods', singular: 'periodo di blackout', defaultSort: ['-fromDate'],
  columns: [{ key: 'name', label: 'Nome' }, dateCol('fromDate', 'Dal'), dateCol('toDate', 'Al'), enumCol('enforcement', 'Modalità', BLACKOUT_ENFORCEMENT_LABEL), boolCol('isActive', 'Attivo')],
  fields: [
    { name: 'name', label: 'Nome', kind: 'text', required: true,
      hint: 'Compare nel messaggio d’errore che il dipendente legge, es. «Il periodo Chiusura estiva è bloccato»: scegli un nome che si spieghi da solo.' },
    { name: 'fromDate', label: 'Dal', kind: 'date', required: true, hint: 'Primo giorno bloccato, incluso.' },
    { name: 'toDate', label: 'Al', kind: 'date', required: true, hint: 'Ultimo giorno bloccato, incluso.' },
    { name: 'description', label: 'Descrizione', kind: 'textarea',
      hint: 'A uso interno: perché il periodo è bloccato. Non viene mostrata al dipendente.' },
    { name: 'enforcement', label: 'Applicazione', kind: 'select', options: BLACKOUT_ENFORCEMENTS, optionLabels: BLACKOUT_ENFORCEMENT_LABEL,
      hint: '«Blocca» rifiuta l’invio; «Avvisa» lascia passare mostrando un avviso. Con «Avvisa» il periodo è una raccomandazione, non un divieto.' },
    { name: 'allowEmergencies', label: 'Consenti emergenze', kind: 'switch',
      hint: 'Se attivo, chi contrassegna la richiesta come urgente può chiedere lo stesso. Il dipendente lo legge nel messaggio di blocco.' },
    { name: 'affectsAllTypes', label: 'Vale per tutti i tipi', kind: 'switch',
      hint: 'Spento, blocca solo i tipi elencati qui sotto. La malattia di solito va lasciata fuori: non è programmabile.' },
    { name: 'affectedLeaveTypes', label: 'Tipi interessati (se non tutti)', kind: 'referenceMany', refType: 'leave-types',
      hint: 'Ignorato quando «Vale per tutti i tipi» è acceso.' },
    { name: 'isActive', label: 'Attivo', kind: 'switch', defaultValue: true,
      hint: 'Spento, il periodo non blocca più nulla ma resta in elenco per riusarlo l’anno prossimo.' },
  ],
};
